/**
 * Toggle/window-independent reservation-check fetch for the bulk-remove
 * reservation-aware warning (offer-unified-table, CU-86cbc3r8b bug fix) —
 * split out of `use-offer-availability-query.ts` to keep that hook under the
 * max-lines-per-function budget. See `use-offer-availability-grids.ts`'s
 * `reservationCheckUnits` doc comment for why this must stay independent of
 * the availability toggle and the rendered/panned window.
 */
import { useOfferUnitAvailability } from '../api/use-offer-unit-availability';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Widest range this fetch will request in one call — stays just under the
 * API's own per-request cap (`MAX_AVAILABILITY_RANGE_DAYS = 400`,
 * `offer-availability.service.ts`) so the request never 400s. Covers the
 * full campaign for the vast majority of offers (well over a year); a
 * campaign longer than this only loses reservation-warning coverage for the
 * portion beyond the first ~13 months from its start date, which is the
 * same period-count ceiling the merged table's own rendered window is
 * bounded by everywhere else.
 */
const RESERVATION_CHECK_MAX_DAYS = 399;

/** Clamps `[from, to]` to at most `RESERVATION_CHECK_MAX_DAYS` days, anchored at `from`. */
function clampReservationCheckRange(from: string, to: string): { from: string; to: string } {
  const fromDate = new Date(`${from}T00:00:00.000Z`);
  const toDate = new Date(`${to}T00:00:00.000Z`);
  const days = Math.floor((toDate.getTime() - fromDate.getTime()) / MS_PER_DAY) + 1;
  if (days <= RESERVATION_CHECK_MAX_DAYS) return { from, to };
  const cappedTo = new Date(fromDate.getTime() + (RESERVATION_CHECK_MAX_DAYS - 1) * MS_PER_DAY);
  return { from, to: cappedTo.toISOString().slice(0, 10) };
}

export interface UseOfferReservationCheckOptions {
  offerId: number;
  rowIds: number[];
  hasCampaignDates: boolean;
  defaultDateFrom: string | null;
  defaultDateTo: string | null;
}

/**
 * Fetches availability for `rowIds` across the offer's full (capped)
 * campaign range, independent of the availability toggle and the rendered
 * window — used ONLY to derive `reservedUnitIds` for the bulk-remove
 * warning, never for the rendered availability columns.
 */
export function useOfferReservationCheck({
  offerId,
  rowIds,
  hasCampaignDates,
  defaultDateFrom,
  defaultDateTo,
}: UseOfferReservationCheckOptions) {
  const range =
    hasCampaignDates && defaultDateFrom !== null && defaultDateTo !== null
      ? clampReservationCheckRange(defaultDateFrom, defaultDateTo)
      : null;
  return useOfferUnitAvailability(offerId, {
    unitIds: range ? rowIds : [],
    from: range?.from ?? '',
    to: range?.to ?? '',
  });
}
